/**
 * Calculus analysis utilities for function behavior analysis.
 * Provides sign analysis which powers monotonicity, concavity, and sign region detection.
 */

import { findRoots, type NumericalConfig } from './numerical-methods';
import type { Interval } from './intervals';
import type { Sign, SignedInterval } from './intervals';

/**
 * Analyze where a function is positive, negative, or zero.
 *
 * This is the core algorithm that powers:
 * - **Monotonicity**: Apply to f'(x) → positive = increasing, negative = decreasing
 * - **Concavity**: Apply to f''(x) → positive = concave up, negative = concave down
 * - **Sign regions**: Apply to f(x) → where function is above/below x-axis
 *
 * Algorithm:
 * 1. Find all roots of f in [xMin, xMax] using numerical methods
 * 2. Create intervals between consecutive roots
 * 3. Sample the midpoint of each interval to determine sign
 *
 * @param f - The function to analyze
 * @param xMin - Left bound of the analysis range
 * @param xMax - Right bound of the analysis range
 * @param config - Optional numerical configuration
 * @returns Array of intervals with their signs, sorted by start position
 *
 * @example
 * // Find where f(x) = x² - 4 is positive/negative
 * const intervals = analyzeSign(x => x*x - 4, -5, 5);
 * // Returns: [
 * //   { start: -5, end: -2, sign: 'positive', ... },
 * //   { start: -2, end: 2, sign: 'negative', ... },
 * //   { start: 2, end: 5, sign: 'positive', ... }
 * // ]
 *
 * @example
 * // Find monotonicity of f(x) = x³ - 3x by analyzing f'(x) = 3x² - 3
 * const fPrime = (x: number) => 3*x*x - 3;
 * const monotonicity = analyzeSign(fPrime, -5, 5);
 * // positive intervals = increasing, negative intervals = decreasing
 */
export function analyzeSign(
  f: (x: number) => number,
  xMin: number,
  xMax: number,
  config?: Partial<NumericalConfig>,
): SignedInterval[] {
  // Find all roots (sign change points) in the range
  const roots = findRoots(f, xMin, xMax, config);

  // Create intervals between consecutive boundaries
  const boundaries = [xMin, ...roots, xMax];
  const intervals: SignedInterval[] = [];

  for (let i = 0; i < boundaries.length - 1; i++) {
    const start = boundaries[i];
    const end = boundaries[i + 1];

    // Skip degenerate intervals
    if (start >= end) {
      continue;
    }

    // Sample at midpoint to determine sign
    const midpoint = (start + end) / 2;
    const value = f(midpoint);

    let sign: Sign;
    if (value > 0) {
      sign = 'positive';
    } else if (value < 0) {
      sign = 'negative';
    } else {
      sign = 'zero';
    }

    intervals.push({
      start,
      end,
      // First interval starts at boundary (open at -∞, closed otherwise)
      startInclusive: i === 0 && isFinite(xMin),
      // Last interval ends at boundary (open at +∞, closed otherwise)
      endInclusive: i === boundaries.length - 2 && isFinite(xMax),
      sign,
    });
  }

  return intervals;
}

/**
 * Type alias for monotonicity analysis results.
 * Positive = increasing, Negative = decreasing.
 */
export type MonotonicityInterval = SignedInterval;

/**
 * Type alias for concavity analysis results.
 * Positive = concave up, Negative = concave down.
 */
export type ConcavityInterval = SignedInterval;

/**
 * Analyze the domain of a function by finding intervals where f(x) returns finite values.
 *
 * Samples the function at evenly-spaced points and groups consecutive finite results
 * into intervals. Useful for visualizing where functions like sqrt(x), log(x), or 1/x
 * are defined.
 *
 * @param fn - The function to analyze
 * @param xMin - Left bound of the analysis range
 * @param xMax - Right bound of the analysis range
 * @param opts - Options: `samples` controls resolution (default 200)
 * @returns Array of intervals where the function produces finite values
 */
export function analyzeDomain(
  fn: (x: number) => number,
  xMin: number,
  xMax: number,
  opts?: { samples?: number },
): Interval[] {
  const samples = opts?.samples ?? 200;
  const step = (xMax - xMin) / samples;
  const intervals: Interval[] = [];
  let start: number | null = null;

  for (let i = 0; i <= samples; i++) {
    const x = xMin + i * step;
    const y = fn(x);

    if (isFinite(y)) {
      if (start === null) {
        start = x;
      }
    } else {
      if (start !== null) {
        intervals.push({
          start,
          end: xMin + (i - 1) * step,
          startInclusive: true,
          endInclusive: true,
        });
        start = null;
      }
    }
  }

  // Close final interval
  if (start !== null) {
    intervals.push({
      start,
      end: xMax,
      startInclusive: true,
      endInclusive: true,
    });
  }

  return intervals;
}

/**
 * Analyze the range (output values) of a function by sampling f(x) across [xMin, xMax],
 * sorting the resulting y-values, and detecting gaps to produce y-intervals.
 *
 * @param fn - The function to analyze
 * @param xMin - Left bound of the x-domain to sample
 * @param xMax - Right bound of the x-domain to sample
 * @param opts - Options: `samples` controls resolution (default 500)
 * @returns Array of y-intervals where the function produces values
 */
export function analyzeRange(
  fn: (x: number) => number,
  xMin: number,
  xMax: number,
  opts?: { samples?: number },
): Interval[] {
  const samples = opts?.samples ?? 500;
  const step = (xMax - xMin) / samples;

  // Collect all finite y-values
  const yValues: number[] = [];
  for (let i = 0; i <= samples; i++) {
    const x = xMin + i * step;
    const y = fn(x);
    if (isFinite(y)) {
      yValues.push(y);
    }
  }

  if (yValues.length === 0) {
    return [];
  }

  // Sort y-values
  yValues.sort((a, b) => a - b);

  const yMin = yValues[0];
  const yMax = yValues[yValues.length - 1];
  const span = yMax - yMin;

  // If all values are essentially the same, return a single degenerate interval
  if (span < 1e-10) {
    return [{ start: yMin, end: yMax, startInclusive: true, endInclusive: true }];
  }

  // Gap threshold: must be large enough to tolerate non-uniform y-value density.
  // Continuous functions (polynomials, trig) have dense samples near extrema and sparse
  // samples at steep regions. The span-relative floor (2%) prevents false gaps there,
  // while the mean-based component (10x) catches real discontinuities (e.g. 1/x at y=0).
  const meanGap = span / yValues.length;
  const threshold = Math.max(meanGap * 10, span * 0.02);

  const intervals: Interval[] = [];
  let intervalStart = yValues[0];

  for (let i = 1; i < yValues.length; i++) {
    if (yValues[i] - yValues[i - 1] > threshold) {
      // Gap detected — close current interval and start new one
      intervals.push({
        start: intervalStart,
        end: yValues[i - 1],
        startInclusive: true,
        endInclusive: true,
      });
      intervalStart = yValues[i];
    }
  }

  // Close final interval
  intervals.push({
    start: intervalStart,
    end: yValues[yValues.length - 1],
    startInclusive: true,
    endInclusive: true,
  });

  return intervals;
}

/**
 * Find monotonicity intervals by analyzing the first derivative.
 * Convenience wrapper around analyzeSign.
 *
 * @param fPrime - The first derivative function f'(x)
 * @param xMin - Left bound of the analysis range
 * @param xMax - Right bound of the analysis range
 * @param config - Optional numerical configuration
 * @returns Array of intervals where positive = increasing, negative = decreasing
 */
export function findMonotonicityIntervals(
  fPrime: (x: number) => number,
  xMin: number,
  xMax: number,
  config?: Partial<NumericalConfig>,
): MonotonicityInterval[] {
  return analyzeSign(fPrime, xMin, xMax, config);
}

/**
 * Find concavity intervals by analyzing the second derivative.
 * Convenience wrapper around analyzeSign.
 *
 * @param fDoublePrime - The second derivative function f''(x)
 * @param xMin - Left bound of the analysis range
 * @param xMax - Right bound of the analysis range
 * @param config - Optional numerical configuration
 * @returns Array of intervals where positive = concave up, negative = concave down
 */
export function findConcavityIntervals(
  fDoublePrime: (x: number) => number,
  xMin: number,
  xMax: number,
  config?: Partial<NumericalConfig>,
): ConcavityInterval[] {
  return analyzeSign(fDoublePrime, xMin, xMax, config);
}
